import { includes, map } from "lodash/fp";

import {
  CONFERENCING_MEETING_PLATFORMS,
  CONFERENCING_PLATFORMS_BY_ID,
  ConferencingPlatform,
} from "@readai/common/constants/platforms";
import { PROVIDERS_WITH_DOCUMENTS } from "@readai/common/constants/providerDocuments";
import {
  EMAIL_PROVIDERS,
  MESSAGING_BOT_PROVIDERS_BY_ID,
  PROVIDERS_BY_ID,
} from "@readai/common/constants/providers";
import type {
  ConferencingMeetingPlatform,
  IntegrationScope,
  Provider,
  ProviderId,
  ProviderWithDocsId,
} from "@readai/common/types";
import {
  DocumentSearchResultMetadata,
  EnterpriseSearchResultItemMetadata,
  EnterpriseSearchSourceProvider,
  ProviderDocumentKind,
  ProviderOwner,
} from "@readai/common/types/enterpriseSearchTypes";

import { withQueryParamsUrl } from "./queryParamUtils";
import { getAppUrl } from "./urlUtils";
import { STAGE } from "../config/constants";

const EMAIL_PROVIDER_IDS = map("id", EMAIL_PROVIDERS);
export const isEmailProvider = (provider?: string) =>
  provider && includes(provider, EMAIL_PROVIDER_IDS);

export const getIntegrationConnectUrl = ({
  action,
  returnParams,
  returnRoute,
  route,
  scope,
}: {
  action: string;
  returnParams?: Parameters<typeof withQueryParamsUrl>[2];
  returnRoute: string;
  route: string;
  scope?: IntegrationScope;
}) =>
  getAppUrl(
    withQueryParamsUrl(route, {
      return_url: getAppUrl(
        withQueryParamsUrl(
          returnRoute,
          { action, success: true },
          returnParams,
        ),
      ),
      error_url: getAppUrl(
        withQueryParamsUrl(
          returnRoute,
          { action, success: false },
          returnParams,
        ),
      ),
      scope,
    }),
  );

export const getSlackConnectUrl = (opts: {
  returnRoute: string;
  scope: IntegrationScope;
}) =>
  getIntegrationConnectUrl({
    action: MESSAGING_BOT_PROVIDERS_BY_ID.slack_bot.connectAction,
    route: MESSAGING_BOT_PROVIDERS_BY_ID.slack_bot.connectUrl,
    ...opts,
  });

export const isConferencingPlatform = (
  provider: ProviderId | ConferencingMeetingPlatform,
): provider is ConferencingMeetingPlatform =>
  includes(provider, CONFERENCING_MEETING_PLATFORMS);

export const getProviderConfig = <T extends ProviderId>(
  provider: T | ConferencingMeetingPlatform,
): ConferencingPlatform | Provider => {
  const config = isConferencingPlatform(provider)
    ? CONFERENCING_PLATFORMS_BY_ID[provider]
    : PROVIDERS_BY_ID[provider];

  if (!config) {
    throw new Error(`Unknown provider: "${provider}"`);
  }

  return config;
};

export const getProviderIconSrc = <T extends ProviderId>(
  provider: T | ConferencingMeetingPlatform,
): string | undefined => {
  try {
    const config = getProviderConfig(provider);
    return config.icon;
  } catch {
    if (STAGE !== "production") {
      throw new Error(
        `Unknown provider: "${provider}" when determining provider icon`,
      );
    }
  }
};

export const getProviderDisplayName = <T extends ProviderId>(
  provider: T | ConferencingMeetingPlatform,
  opts?: { short?: boolean },
): string | undefined => {
  try {
    const config = getProviderConfig(provider);
    return opts?.short ? config.displayNameShort : config.displayName;
  } catch {
    if (STAGE !== "production") {
      throw new Error(
        `Unknown provider: "${provider}" when determining display name`,
      );
    }
  }
};

export const isProviderWithDocuments = (
  provider: EnterpriseSearchSourceProvider,
): provider is ProviderWithDocsId =>
  includes(provider, PROVIDERS_WITH_DOCUMENTS);

export const getDocumentKind = (
  provider: EnterpriseSearchSourceProvider,
  metadata: EnterpriseSearchResultItemMetadata | undefined,
) => {
  if (
    isProviderWithDocuments(provider) &&
    metadata &&
    "providerDocumentKind" in metadata
  ) {
    return metadata.providerDocumentKind as ProviderDocumentKind;
  }
  return undefined;
};

export const getDocumentOwners = (
  provider: EnterpriseSearchSourceProvider,
  metadata: EnterpriseSearchResultItemMetadata | undefined,
) => {
  if (
    isProviderWithDocuments(provider) &&
    metadata &&
    "providerOwners" in metadata
  ) {
    return metadata.providerOwners as ProviderOwner[];
  }
  return undefined;
};

export const getDocumentPath = (
  provider: EnterpriseSearchSourceProvider,
  metadata: EnterpriseSearchResultItemMetadata | undefined,
) => {
  if (
    isProviderWithDocuments(provider) &&
    metadata &&
    "providerDocumentPath" in metadata
  ) {
    return metadata.providerDocumentPath as DocumentSearchResultMetadata["providerDocumentPath"];
  }
  return undefined;
};
